import './i18n';
import UpdatePassword from './features/auth/UpdatePassword';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useParams, useLocation } from 'react-router-dom'; 
import axios from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet';

import useVisitorTracking from './hooks/useVisitorTracking'; 
import { supabase } from './lib/supabase';

// --- CONTEXTS ---
import { AuthProvider, useAuth } from './contexts/AuthContext';

// --- COMPONENTS ---
import NavBar from './components/NavBar';
import PrivateRoute from './components/PrivateRoute';
import CoreAndShellPillar from './components/CoreAndShellPillar';
import SupplierLanding from './components/SupplierLanding'; 
import BuyerLanding from './components/BuyerLanding'; 

// --- FEATURES ---
import LoginForm from './features/auth/LoginForm';
import RegisterForm from './features/auth/RegisterForm';
import SupplierDirectory from './features/canada/suppliers/SupplierDirectory';
import SupplierOnboarding from './features/canada/pages/SupplierOnboarding';
import SmartListingForm from './features/canada/pages/SmartListingForm';
import AdminDashboard from './features/admin/AdminDashboard';
import ProductDetail from './features/products/ProductDetail';
import SupplierPortal from './features/canada/pages/SupplierPortal';
import SupplierPublicProfile from './features/canada/pages/SupplierPublicProfile';
import TradeSimulator from './features/canada/pages/TradeSimulator';
import RfqForm from './features/canada/pages/RfqForm';
import RfqInviteTeaser from './features/canada/pages/RfqInviteTeaser';
import SupplierAnalytics from './features/canada/pages/SupplierAnalytics';
import SubscriptionManager from './features/payments/SubscriptionManager';
import SupplierInbox from './features/canada/pages/SupplierInbox';
import AccountSettings from './features/auth/AccountSettings';
import SeoDirectory from './features/canada/pages/SeoDirectory'; 
import MaterialProfile from './features/canada/pages/MaterialProfile';

// ⚡ NEW CMS FEATURE
import ResourceList from './features/resources/ResourceList';
import ResourceDetail from './features/resources/ResourceDetail';

import './App.css';
import TermsOfService from './features/legal/TermsOfService';
import PrivacyPolicy from './features/legal/PrivacyPolicy';

const AppTracker = () => {
    useVisitorTracking(); 
    return null;
};

// ⚡ SEO FIX: Centralized SEO & Multilingual URL Wrapper
const LanguageWrapper = ({ lang, children }) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    // Force the i18n engine to match the URL route
    useEffect(() => {
        if (i18n.language !== lang) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    // Calculate the clean path for the hreflang tags
    const cleanPath = location.pathname.replace(/^\/fr/, '') || '/';
    const enUrl = `https://www.tariffbeater.com${cleanPath}`;
    const frUrl = `https://www.tariffbeater.com/fr${cleanPath === '/' ? '' : cleanPath}`;
    const canonicalUrl = lang === 'fr' ? frUrl : enUrl;

    return (
        <>
            <Helmet>
                <html lang={lang} />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="alternate" hreflang="en-ca" href={enUrl} />
                <link rel="alternate" hreflang="fr-ca" href={frUrl} />
                <link rel="alternate" hreflang="x-default" href={enUrl} />
            </Helmet>
            {children}
        </>
    );
};

const MaterialProfileWrapper = () => {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            const { data: material, error } = await supabase
                .from('seo_landing_pages') 
                .select('*')
                .eq('slug', slug)
                .single();
            
            if (error) console.error("Error fetching material:", error);

            if (material && material.hs_chapter) {
                const { data: siblings, error: siblingsError } = await supabase
                    .from('seo_landing_pages')
                    .select('id, master_keyword, slug')
                    .eq('hs_chapter', material.hs_chapter)
                    .neq('slug', slug)
                    .limit(3);
                
                if (siblingsError) {
                    console.error("Error fetching siblings:", siblingsError);
                } else if (siblings) {
                    setRelated(siblings);
                }
            }
            
            setData(material);
            setLoading(false);
        }
        fetchData();
    }, [slug]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-medium">Loading material profile...</div>;
    if (!data) return <div className="min-h-screen flex items-center justify-center text-rose-600 font-bold">Material not found.</div>;

    return <MaterialProfile materialData={data} relatedMaterials={related} />;
};

const SupplierOnboardingWrapper = () => {
    const { user } = useAuth();
    return user ? <SupplierOnboarding userId={user.id} /> : <Navigate to="/login" />;
};

const Home = () => {
    const { t } = useTranslation(); 
    return (
    <div className="flex flex-col items-center p-4 antialiased bg-gradient-to-b from-blue-50/50 to-slate-50 w-full">
        
        {/* ⚡ SEO FIX: Dynamic Title and Description Translations */}
        <Helmet>
            <title>{t('seo.home_title')}</title>
            <meta name="description" content={t('seo.home_desc')} />
        </Helmet>

        <div className="text-center p-12 bg-white rounded-t-2xl shadow-sm max-w-5xl mx-auto mt-6 w-full border-b border-slate-200">
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight leading-tight">
                <span className="block text-xl md:text-2xl text-blue-600 font-bold mb-3 tracking-widest uppercase">
                    <Trans i18nKey="home.hero_kicker">Build Smarter. Beat the Border.</Trans>
                </span>
                
                {/* ⚡ FIX: Hero Title */}
                <Trans 
                    i18nKey="home.hero_title"
                    components={{ 1: <span className="text-blue-600" /> }}
                />
            </h1>
            <p className="mt-6 text-xl text-slate-600 max-w-3xl mx-auto font-light">
                {/* ⚡ FIX: Hero Subtext */}
                <Trans 
                    i18nKey="home.hero_subtext"
                    components={{ 1: <span className="font-semibold text-slate-800" /> }}
                />
            </p>

            <div className="mt-10">
                <Link to="/register?promo=shell-core-2000&modules=premium_supplier,pro_buyer_standard" className="bg-blue-600 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-lg hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 inline-block">
                    {t('home.cta_btn', 'Claim Your Shortlist Spot →')}
                </Link>
            </div>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                <div className="p-6 bg-white rounded-xl border border-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200">
                    <h3 className="font-bold text-slate-900 flex items-center gap-3">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
                        {t('home.feat_direct_title', 'Manufacturer Direct')}
                    </h3>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                        {t('home.feat_direct_desc', 'Bypass intermediaries and access wholesale pricing direct from fabricators of structural metals, raw minerals, commercial lumber, and architectural finishes.')}
                    </p>
                </div>
                <div className="p-6 bg-white rounded-xl border border-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200">
                    <h3 className="font-bold text-slate-900 flex items-center gap-3">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                        {t('home.feat_reliable_title', 'Pre-Qualified Connections')}
                    </h3>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                        {t('home.feat_reliable_desc', 'Source and sell with confidence. Our network consists exclusively of 100% verified Canadian businesses with active federal or provincial corporate standing.')}
                    </p>
                </div>
                <div className="p-6 bg-white rounded-xl border border-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200">
                    <h3 className="font-bold text-slate-900 flex items-center gap-3">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                        {t('home.feat_landed_title', 'Landed Cost Intelligence')}
                    </h3>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">{t('home.feat_landed_desc', 'Instant CFTA, Tax and Freight simulation. Know your total cost before you issue the PO.')}</p>
                </div>
            </div>
        </div>

        <div className="w-full max-w-5xl mx-auto py-16 px-8 text-center bg-white border-x border-slate-200 relative overflow-hidden">
            <h3 className="text-xs font-bold text-red-600 uppercase tracking-[0.3em] mb-4">{t('home.mission_label', 'The National Mission')}</h3>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-8">
                {t('home.mission_title', 'Making Canada Stronger.')}
            </h2>
            <div className="max-w-3xl mx-auto">
                <p className="text-slate-700 text-lg md:text-xl font-medium leading-relaxed mb-10">
                    {/* ⚡ FIX: Mission Description */}
                    <Trans 
                        i18nKey="home.mission_desc"
                        components={{ 1: <span className="text-blue-800 font-bold border-b-2 border-red-500 pb-0.5" /> }}
                    />
                </p>
                <div className="flex flex-col items-center mt-4">
                    <p className="text-sm font-bold text-blue-600 tracking-widest uppercase">{t('home.canadian_advantage', 'The Canadian Advantage')}</p>
                </div>
            </div>
        </div>

        <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 w-full max-w-5xl mx-auto rounded-2xl shadow-2xl overflow-hidden text-left mb-12 border border-slate-800">
            <div className="p-8 md:p-14">
                <h2 className="text-3xl md:text-4xl font-black text-white mb-6 tracking-tight">{t('home.spec_title', 'The Core & Shell Specialist')}</h2>
                <p className="text-slate-300 text-lg max-w-3xl mb-12 leading-relaxed font-light">
                    {t('home.spec_desc', 'Unlike generalist marketplaces, TariffBeater is engineered exclusively for the commercial Core & Shell sector. This hyper-focus delivers superior supplier depth and targeted CFTA compliance insights across all foundational building materials.')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border-t-4 border-t-emerald-500 border-x border-b border-slate-700/50 flex flex-col h-full shadow-inner">
                        <h3 className="text-emerald-400 font-bold text-sm tracking-widest uppercase mb-6 flex items-center gap-3 border-b border-slate-700 pb-4">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                            {t('home.approved_label', 'Approved for Trade')}
                        </h3>
                        <ul className="space-y-6 text-sm flex-grow">
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-200 tracking-wide">{t('home.cat_metals', 'Structural Metals')}</span>
                                <span className="text-slate-400">{t('home.cat_metals_desc', '(HS 72, 73, 76) — Beams, Rebar, Aluminum')}</span>
                            </li>
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-200 tracking-wide">{t('home.cat_minerals', 'Raw Minerals')}</span>
                                <span className="text-slate-400">{t('home.cat_minerals_desc', '(HS 25, 68) — Ready-mix Concrete, Cement, Stone')}</span>
                            </li>
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-200 tracking-wide">{t('home.cat_wood', 'Lumber & Wood')}</span>
                                <span className="text-slate-400">{t('home.cat_wood_desc', '(HS 44) — Dimensional Lumber, Plywood, CLT')}</span>
                            </li>
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-200 tracking-wide">{t('home.cat_finishes', 'Finishes')}</span>
                                <span className="text-slate-400">{t('home.cat_finishes_desc', '(HS 32, 69, 70) — Paint, Ceramic Tiles, Flat Glass')}</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border-t-4 border-t-rose-500 border-x border-b border-slate-700/50 flex flex-col h-full relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-rose-900/10 to-transparent pointer-events-none"></div>
                        <h3 className="text-rose-400 font-bold text-sm tracking-widest uppercase mb-6 flex items-center gap-3 border-b border-slate-700 pb-4 relative z-10">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                            {t('home.prohibited_label', 'Strictly Prohibited')}
                        </h3>
                        <ul className="space-y-6 text-sm flex-grow relative z-10">
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-300 tracking-wide">{t('home.cat_mep', 'MEP Machinery')}</span>
                                <span className="text-slate-500">{t('home.cat_mep_desc', '(HS 84) — HVAC, Boilers, Pumps, Compressors')}</span>
                            </li>
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-300 tracking-wide">{t('home.cat_elec', 'Electrical Systems')}</span>
                                <span className="text-slate-500">{t('home.cat_elec_desc', '(HS 85) — Breakers, Switchgear, Motors')}</span>
                            </li>
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-300 tracking-wide">{t('home.cat_inst', 'Precision Instruments')}</span>
                                <span className="text-slate-500">{t('home.cat_inst_desc', '(HS 90) — Smart Controls, Flow Meters')}</span>
                            </li>
                            <li className="flex flex-col gap-1">
                                <span className="font-semibold text-slate-300 tracking-wide">{t('home.cat_furn', 'Furniture & Fit-outs')}</span>
                                <span className="text-slate-500">{t('home.cat_furn_desc', '(HS 94) — Luminaires, Prefab Cabins')}</span>
                            </li>
                        </ul>
                    </div>

                </div>
            </div>
        </div>

        <div className="w-full max-w-5xl mx-auto py-12 px-8 md:px-14 bg-white border border-slate-200 text-left rounded-2xl mb-16 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                {t('home.seo_title', 'How Our B2B Procurement Platform Works')}
            </h2>
            <div className="text-slate-600 space-y-5 leading-relaxed">
                <p>
                    {/* ⚡ FIX: SEO Paragraph 1 */}
                    <Trans 
                        i18nKey="home.seo_p1"
                        components={{ 1: <strong /> }}
                    />
                </p>
                <p>
                    {/* ⚡ FIX: SEO Paragraph 2 */}
                    <Trans 
                        i18nKey="home.seo_p2"
                        components={{ 1: <strong /> }}
                    />
                </p>
                <p>
                    {/* ⚡ FIX: SEO Paragraph 3 */}
                    <Trans 
                        i18nKey="home.seo_p3"
                        components={{ 1: <strong /> }}
                    />
                </p>
                <p>
                    {/* seo_p4 doesn't have tags in JSON, so standard t() works, but Trans is fine too */}
                    <Trans i18nKey="home.seo_p4" />
                </p>
            </div>
        </div>

    </div>
    );
};

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'
});

// ⚡ SEO FIX: Extracted all core routes so they can be securely mounted to multiple language prefixes
const CoreRoutes = () => {
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/directory" element={<SupplierDirectory api={api} />} />
            <Route path="/suppliers" element={<SupplierLanding />} />
            <Route path="/buyers" element={<BuyerLanding />} />
            <Route path="/sourcing-directory" element={<SeoDirectory />} />
            <Route path="/assemblies/commercial-core-and-shell-canada" element={<CoreAndShellPillar />} />
            <Route path="/materials/:slug" element={<MaterialProfileWrapper />} />
            
            {/* ⚡ NEW CMS DYNAMIC ROUTES - Captures both EN and FR naturally */}
            <Route path="/resources" element={<ResourceList />} />
            <Route path="/resources/:slug" element={<ResourceDetail />} />

            <Route path="/suppliers/:id" element={<SupplierPublicProfile />} />
            <Route path="/rfq/new" element={<RfqForm />} />
            <Route path="/invite" element={<RfqInviteTeaser />} />
            <Route path="/simulator" element={<PrivateRoute><TradeSimulator /></PrivateRoute>} />
            <Route path="/account" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
            <Route path="/supplier-onboarding" element={<PrivateRoute><SupplierOnboardingWrapper /></PrivateRoute>} />
            <Route path="/products/new" element={<PrivateRoute><SmartListingForm /></PrivateRoute>} />
            <Route path="/products/:id" element={<PrivateRoute><ProductDetail /></PrivateRoute>} />
            <Route path="/inventory" element={<PrivateRoute><SupplierPortal /></PrivateRoute>} />
            <Route path="/inbox" element={<PrivateRoute><SupplierInbox /></PrivateRoute>} />
            <Route path="/analytics" element={<PrivateRoute><SupplierAnalytics /></PrivateRoute>} />
            <Route path="/billing" element={<SubscriptionManager />} />
            <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
            <Route path="/update-password" element={<UpdatePassword />} />
        </Routes>
    );
};

export default function App() {
    const { t, i18n } = useTranslation();
    const [systemStatus, setSystemStatus] = useState({ api: 'Checking...', db: 'Checking...' });
    
    // Check if current language is French for the footer links
    const isFr = i18n.language === 'fr';

    useEffect(() => {
        const checkHealth = async () => {
            try {
                const res = await api.get('/health');
                const isDbConnected = res.data.database === 'connected' || res.data.supabaseConnected === true;
                setSystemStatus({
                    api: res.data.status === 'ok' ? 'Online' : res.data.status,
                    db: isDbConnected ? 'Connected' : 'Error'
                });
            } catch (err) {
                setSystemStatus({ api: 'Offline', db: 'Unknown' });
            }
        };
        checkHealth();
    }, []);

    return (
        <Router>
            <AuthProvider>
                <AppTracker />
                
                <div className="min-h-screen w-full bg-slate-50 flex flex-col font-sans">
                    <NavBar />
                    <main className="flex-grow container mx-auto px-4 py-8">
                        <Routes>
                            {/* ⚡ SEO FIX: Render app under /fr prefix */}
                            <Route path="/fr/*" element={
                                <LanguageWrapper lang="fr">
                                    <CoreRoutes />
                                </LanguageWrapper>
                            } />
                            
                            {/* ⚡ SEO FIX: Render app under root (English) */}
                            <Route path="/*" element={
                                <LanguageWrapper lang="en">
                                    <CoreRoutes />
                                </LanguageWrapper>
                            } />
                        </Routes>
                    </main>

                    <footer className="bg-white border-t border-slate-200 py-8 mt-auto">
                        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
                            <div className="mb-4 md:mb-0">
                                © 2026 Tariffbeater Inc. v3.2-beta | 
                                <a href={isFr ? "/fr/resources" : "/resources"} className="ml-3 hover:text-blue-600 font-medium transition-colors">
                                    {isFr ? 'Ressources' : 'Resources'}
                                </a> |
                                <a href="/sourcing-directory" className="ml-3 hover:text-blue-600 font-medium transition-colors">Sourcing Directories</a> |
                                <a href="/terms" className="ml-3 hover:text-blue-600 font-medium transition-colors">{t('common.terms')}</a> |
                                <a href="/privacy" className="ml-3 hover:text-blue-600 font-medium transition-colors">{t('common.privacy')}</a>
                            </div>
                            <div className="flex space-x-6">
                                <div className="flex items-center bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
                                    <span className={`h-2.5 w-2.5 rounded-full mr-2 shadow-sm ${systemStatus.api === 'Online' ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                                    <span className="font-medium">API: {systemStatus.api}</span>
                                </div>
                                <div className="flex items-center bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
                                    <span className={`h-2.5 w-2.5 rounded-full mr-2 shadow-sm ${systemStatus.db === 'Connected' ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                                    <span className="font-medium">DB: {systemStatus.db}</span>
                                </div>
                            </div>
                        </div>
                    </footer>
                </div>
            </AuthProvider>
        </Router>
    );
}